import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import {
	Check,
	Info,
	Layers,
	Rocket,
	Share2,
	User,
	Workflow,
} from "lucide-react";
import { useState } from "react";
import { AgentLogoImage } from "@/components/agents/AgentLogoImage";
import { CtaBlock } from "@/components/CtaBlock";
import { JsonLd } from "@/components/JsonLd";
import { NotFoundView } from "@/components/NotFoundView";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useFileUrl } from "@/hooks/use-file-url";
import { trackEvent } from "@/lib/analytics";
import { currencyAtom } from "@/lib/currency";
import {
	formatCompactCurrency,
	formatCurrency as formatCurrencyUtil,
} from "@/lib/format";
import { founderProfileQueryOptions } from "@/lib/founder-profile.query";
import { breadcrumbListSchema, profilePageSchema } from "@/lib/schema";
import {
	buildLocalizedSeoLinks,
	dynamicFounderOgUrl,
	getLocalizedSeoUrls,
} from "@/lib/seo";
import { xAvatarUrl } from "@/lib/validation";

type FounderProfile = {
	agents: Array<{
		allTimeRevenue: number;
		categories?: string[];
		description: string;
		displayDescription: string;
		displayName: string;
		displayPathSegment: string;
		displayWebsite?: string | null;
		estimatedMrr: number;
		id: string;
		logoUrl?: string | null;
		revenueD30: number;
		slug: string;
	}>;
	avatarUrl: string | null;
	userImage: string | null;
	id: string;
	name: string;
	slug: string;
	storyBio: string | null;
	storyNext: string | null;
	storyStack: string | null;
	storyWorkingStyle: string | null;
	tagline: string | null;
	xHandle: string | null;
	stats: {
		allTimeRevenue: number;
		estimatedMrr: number;
		revenueD30: number;
		totalAgents: number;
	};
	updatedAt: string | Date;
};

const FOUNDER_AGENT_SKELETON_KEYS = [
	"agent-skel-1",
	"agent-skel-2",
	"agent-skel-3",
];
const FOUNDER_AGENT_SKELETON_METRIC_KEYS = [
	"agent-skel-metric-1",
	"agent-skel-metric-2",
	"agent-skel-metric-3",
];

export const Route = createFileRoute("/founders/$founderSlug")({
	component: FounderProfilePage,
	loader: ({ context, params }) =>
		context.queryClient.ensureQueryData(
			founderProfileQueryOptions(params.founderSlug),
		),
	head: ({ params, matches, loaderData }) => {
		const name = params.founderSlug
			.split("-")
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
			.join(" ");
		const internalPath = `/founders/${params.founderSlug}`;
		const { canonicalUrl } = getLocalizedSeoUrls({ matches, internalPath });
		const desc = `${name}'s AI agents on AgentMRR - provider-verified MRR, total revenue, and founder ranking across all listed agents.`;
		const ogImage = dynamicFounderOgUrl(
			params.founderSlug,
			loaderData?.founder?.stats?.estimatedMrr,
		);
		return {
			meta: [
				{ title: `${name} - AgentMRR` },
				{ name: "description", content: desc },
				{ property: "og:title", content: `${name} - AgentMRR` },
				{ property: "og:description", content: desc },
				{ property: "og:image", content: ogImage },
				{ property: "og:image:width", content: "1200" },
				{ property: "og:image:height", content: "630" },
				{ property: "og:url", content: canonicalUrl },
				{ property: "og:type", content: "profile" },
				{ property: "og:site_name", content: "AgentMRR" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:site", content: "@agentmrr" },
				{ name: "twitter:title", content: `${name} - AgentMRR` },
				{ name: "twitter:description", content: desc },
				{ name: "twitter:image", content: ogImage },
				{ name: "twitter:image:alt", content: `${name} founder revenue card` },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath }),
		};
	},
});

function FounderProfilePage() {
	const { founderSlug } = Route.useParams();
	const [copied, setCopied] = useState(false);
	const currency = useAtomValue(currencyAtom);
	const founderQuery = useQuery(founderProfileQueryOptions(founderSlug));

	const founder = (founderQuery.data?.founder ?? null) as FounderProfile | null;
	const previewFounder =
		!founder && !founderQuery.isLoading && import.meta.env.DEV
			? buildPreviewFounder(founderSlug)
			: null;
	const resolvedFounder = founder ?? previewFounder;
	const isLoading = founderQuery.isLoading && !resolvedFounder;

	if (!isLoading && !resolvedFounder) {
		return (
			<NotFoundView
				label="Founder"
				description="No public profile exists for this slug."
			/>
		);
	}

	if (resolvedFounder && typeof document !== "undefined") {
		const founderStartupCount = resolvedFounder.agents.length;
		document.title = `${resolvedFounder.name} - ${founderStartupCount} startup${founderStartupCount !== 1 ? "s" : ""} · ${formatCurrencyUtil(resolvedFounder.stats.allTimeRevenue, currency)} revenue | AgentMRR`;
	}

	function handleShare() {
		navigator.clipboard.writeText(window.location.href);
		trackEvent("founder_shared", { founder_slug: founderSlug });
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}

	const founderPath = resolvedFounder
		? `/founders/${resolvedFounder.slug}`
		: null;

	return (
		<PublicPageLayout>
			{resolvedFounder && founderPath ? (
				<JsonLd
					data={[
						breadcrumbListSchema([
							{ name: "Home", path: "/" },
							{ name: "Founders", path: "/founders" },
							{ name: resolvedFounder.name, path: founderPath },
						]),
						profilePageSchema({
							name: resolvedFounder.name,
							description: `Verified revenue across ${resolvedFounder.stats.totalAgents} ${resolvedFounder.stats.totalAgents === 1 ? "agent" : "agents"} tracked on AgentMRR.`,
							path: founderPath,
							image:
								resolvedFounder.avatarUrl ??
								resolvedFounder.userImage ??
								undefined,
						}),
					]}
				/>
			) : null}
			<main className="mx-auto w-full max-w-5xl pt-4 pb-16 sm:pt-6">
				<PageBreadcrumb
					items={[
						{ label: "Founders", to: "/founders" },
						{ label: resolvedFounder?.name ?? "Founder" },
					]}
				/>
				{/* Hero */}
				<section className="mt-10">
					<div className="pb-3">
						<span className="inline-flex items-center gap-1.5 self-start font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
							<FounderLivePulse />
							<span className="text-foreground">Live</span>
						</span>
					</div>

					<div className="flex flex-col gap-8 border-t border-border pt-3 @3xl/page:flex-row @3xl/page:items-start @3xl/page:justify-between">
						<div className="flex min-w-0 items-center gap-5 @md/page:gap-7">
							{resolvedFounder ? (
								<FounderHeroAvatar
									avatarUrl={
										resolvedFounder.avatarUrl ??
										resolvedFounder.userImage ??
										xAvatarUrl(resolvedFounder.xHandle) ??
										null
									}
									name={resolvedFounder.name}
								/>
							) : (
								<div className="size-16 shrink-0 animate-pulse border border-border bg-muted @md/page:size-20" />
							)}
							<div className="min-w-0 space-y-2.5">
								<h1
									className="font-mono text-[clamp(1.75rem,4vw,2.75rem)] leading-[1.05] tracking-[-0.02em] text-foreground"
									style={{ fontWeight: 400 }}
								>
									{resolvedFounder ? (
										resolvedFounder.name
									) : (
										<span className="inline-block h-[1em] w-56 max-w-full animate-pulse bg-muted align-middle @md/page:w-72" />
									)}
								</h1>
								<p className="max-w-2xl text-base leading-[1.6] text-muted-foreground">
									{resolvedFounder ? (
										(resolvedFounder.tagline ?? (
											<>
												Verified revenue across{" "}
												{resolvedFounder.stats.totalAgents === 1
													? "one agent"
													: `${resolvedFounder.stats.totalAgents} agents`}{" "}
												tracked in public on AgentMRR.
											</>
										))
									) : (
										<span className="inline-block h-[1.4em] w-full max-w-md animate-pulse bg-muted align-middle" />
									)}
								</p>
							</div>
						</div>

						<div className="flex shrink-0 flex-wrap items-center gap-2">
							<button
								type="button"
								onClick={handleShare}
								disabled={!resolvedFounder}
								className="inline-flex min-h-10 items-center gap-2 border border-border bg-background px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:border-brand/40 hover:text-brand disabled:opacity-50"
							>
								{copied ? (
									<>
										<Check className="size-3.5 text-brand" />
										Copied
									</>
								) : (
									<>
										<Share2 className="size-3.5" />
										Share
									</>
								)}
							</button>
							{resolvedFounder?.xHandle ? (
								<a
									href={`https://x.com/${resolvedFounder.xHandle}`}
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Visit X Profile"
									onClick={() =>
										trackEvent("founder_x_profile_visited", {
											founder_slug: founderSlug,
											x_handle: resolvedFounder.xHandle ?? "",
										})
									}
									className="inline-flex min-h-10 items-center gap-2 border border-border bg-background px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:border-brand/40 hover:text-brand"
								>
									Visit
									<svg
										className="size-3.5"
										viewBox="0 0 1200 1227"
										fill="currentColor"
										aria-hidden="true"
									>
										<path d="M714.163 519.284 1160.89 0h-105.86L667.137 450.887 357.328 0H0l468.492 681.821L0 1226.37h105.866l409.625-476.151 327.181 476.151H1200L714.137 519.284h.026ZM569.165 687.828l-47.468-67.894-377.686-540.2H306.615l304.797 435.991 47.468 67.894 396.2 566.721H892.476L569.165 687.854v-.026Z" />
									</svg>
									Profile
								</a>
							) : null}
						</div>
					</div>
				</section>

				<section className="mt-12">
					<div className="grid grid-cols-2 gap-px border-y border-border bg-border @2xl/page:grid-cols-4">
						<FounderTickerCell
							label="Total revenue"
							value={
								resolvedFounder
									? formatCurrencyUtil(
											resolvedFounder.stats.allTimeRevenue,
											currency,
										)
									: null
							}
							sub="Across all agents"
						/>
						<FounderTickerCell
							label="Last 30 days"
							value={
								resolvedFounder
									? formatCurrencyUtil(
											resolvedFounder.stats.revenueD30,
											currency,
										)
									: null
							}
							sub="Recent revenue"
						/>
						<FounderTickerCell
							label="MRR (est.)"
							value={
								resolvedFounder
									? formatCurrencyUtil(
											resolvedFounder.stats.estimatedMrr,
											currency,
										)
									: null
							}
							sub="Across all agents"
							infoLabel="Estimated from active subscriptions, so it may differ from the provider's actual MRR due to prorations, trials, and billing cycles. Total revenue is more accurate."
						/>
						<FounderTickerCell
							label="Agents"
							value={
								resolvedFounder
									? String(resolvedFounder.stats.totalAgents)
									: null
							}
							sub="Active"
						/>
					</div>
				</section>

				{resolvedFounder ? (
					<FounderStorySection founder={resolvedFounder} />
				) : null}

				<section className="mt-14">
					<div className="flex items-baseline justify-between pb-6">
						<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							{resolvedFounder ? (
								<>&gt; Agents by {resolvedFounder.name}</>
							) : (
								<span className="inline-flex items-center gap-2">
									&gt; Agents by{" "}
									<span className="inline-block h-3 w-24 animate-pulse bg-muted align-middle" />
								</span>
							)}
						</span>
					</div>

					<div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
						{resolvedFounder
							? resolvedFounder.agents.map((agent) => (
									<Link
										key={agent.id}
										to="/agents/$agentSlug"
										params={{ agentSlug: agent.displayPathSegment }}
										className="group flex flex-col border border-border bg-card p-4 transition-colors hover:border-brand/40 hover:bg-brand-soft"
									>
										<div className="flex items-center gap-3">
											<AgentLogoImage
												logoUrl={agent.logoUrl}
												website={agent.displayWebsite}
												mode={agent.displayAvatarMode}
												name={agent.displayInitials}
												size="sm"
											/>
											<p
												className="truncate text-base text-foreground transition-colors group-hover:text-brand"
												style={{ fontWeight: 400 }}
											>
												{agent.displayName}
											</p>
										</div>

										<p className="mt-3 mb-4 line-clamp-2 text-sm leading-[1.55] text-muted-foreground">
											{agent.displayDescription}
										</p>

										<div className="mt-auto grid grid-cols-3 gap-3 border-t border-border pt-4">
											<div className="flex flex-col gap-0.5">
												<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
													30d
												</span>
												<span className="whitespace-nowrap font-mono text-base tabular-nums text-foreground">
													{formatCompactCurrency(agent.revenueD30, currency)}
												</span>
											</div>
											<div className="flex flex-col gap-0.5">
												<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
													MRR
												</span>
												<span className="whitespace-nowrap font-mono text-base tabular-nums text-foreground">
													{formatCompactCurrency(agent.estimatedMrr, currency)}
												</span>
											</div>
											<div className="flex flex-col gap-0.5">
												<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
													Total
												</span>
												<span className="whitespace-nowrap font-mono text-base tabular-nums text-foreground">
													{formatCompactCurrency(
														agent.allTimeRevenue,
														currency,
													)}
												</span>
											</div>
										</div>
									</Link>
								))
							: FOUNDER_AGENT_SKELETON_KEYS.map((key) => (
									<FounderAgentCardSkeleton key={key} />
								))}
					</div>
				</section>
				{resolvedFounder ? <CtaBlock /> : null}
				<div className="mt-16">
					<PublicFooter />
				</div>
			</main>
		</PublicPageLayout>
	);
}

function FounderAgentCardSkeleton() {
	return (
		<div className="flex flex-col border border-border bg-card p-4">
			<div className="flex items-center gap-3">
				<div className="size-10 shrink-0 animate-pulse border border-border bg-muted" />
				<div className="h-4 w-24 animate-pulse bg-muted" />
			</div>
			<div className="mt-3 mb-4 space-y-1">
				<div className="h-[calc(12px*1.55)] w-full animate-pulse bg-muted" />
				<div className="h-[calc(12px*1.55)] w-4/5 animate-pulse bg-muted" />
			</div>
			<div className="mt-auto grid grid-cols-3 gap-3 border-t border-border pt-4">
				{FOUNDER_AGENT_SKELETON_METRIC_KEYS.map((key) => (
					<div key={key} className="flex flex-col gap-0.5">
						<div className="h-2.5 w-8 animate-pulse bg-muted" />
						<div className="h-[13px] w-14 animate-pulse bg-muted" />
					</div>
				))}
			</div>
		</div>
	);
}

function buildPreviewFounder(founderSlug: string): FounderProfile {
	const founderName = founderSlug
		.split("-")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");

	return {
		agents: [
			{
				allTimeRevenue: 74_743_509,
				categories: ["productivity"],
				description:
					"Stan enables people to make a living and work for themselves.",
				displayDescription:
					"Stan enables people to make a living and work for themselves.",
				displayName: "Stan",
				displayPathSegment: "stan",
				estimatedMrr: 3_586_836,
				id: "preview-agent-one",
				logoUrl: null,
				revenueD30: 2_842_781,
				slug: "stan",
			},
		],
		avatarUrl: null,
		userImage: null,
		id: founderSlug,
		name: founderName,
		slug: founderSlug,
		storyBio: null,
		storyNext: null,
		storyStack: null,
		storyWorkingStyle: null,
		tagline: null,
		xHandle: null,
		stats: {
			allTimeRevenue: 74_743_509,
			estimatedMrr: 3_586_836,
			revenueD30: 2_842_781,
			totalAgents: 1,
		},
		updatedAt: new Date(2026, 2, 29).toISOString(),
	};
}

function FounderHeroAvatar({
	avatarUrl,
	name,
}: {
	avatarUrl: string | null;
	name: string;
}) {
	const { url: resolvedAvatarUrl } = useFileUrl(avatarUrl);
	const [failedAvatarUrl, setFailedAvatarUrl] = useState<string | null>(null);

	if (resolvedAvatarUrl && failedAvatarUrl !== resolvedAvatarUrl) {
		return (
			<img
				src={resolvedAvatarUrl}
				alt={name}
				className="size-16 shrink-0 border border-border bg-muted object-cover @md/page:size-20"
				onError={() => setFailedAvatarUrl(resolvedAvatarUrl)}
				referrerPolicy="no-referrer"
			/>
		);
	}

	return (
		<div
			className="inline-flex size-16 shrink-0 items-center justify-center border border-border bg-muted font-mono text-xl uppercase text-foreground @md/page:size-20"
			style={{ fontWeight: 400 }}
		>
			{name
				.split(/\s+/)
				.filter(Boolean)
				.slice(0, 2)
				.map((part) => part[0]?.toUpperCase())
				.join("")}
		</div>
	);
}

function FounderTickerCell({
	label,
	value,
	sub,
	infoLabel,
}: {
	label: string;
	value: string | null;
	sub: string;
	infoLabel?: string;
}) {
	return (
		<div className="flex flex-col gap-1.5 bg-background px-4 py-5 @2xl/page:px-6 @2xl/page:py-6 @2xl/page:first:pl-0">
			<span className="flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
				{label}
				{infoLabel ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<Info className="size-3 shrink-0 cursor-help text-muted-foreground" />
						</TooltipTrigger>
						<TooltipContent className="max-w-64">{infoLabel}</TooltipContent>
					</Tooltip>
				) : null}
			</span>
			<span
				className="mt-4 font-mono text-[clamp(1.125rem,2vw,1.5rem)] leading-none tabular-nums text-foreground"
				style={{ fontWeight: 400 }}
			>
				{value ?? (
					<span className="inline-block h-[1em] w-20 animate-pulse bg-muted align-middle" />
				)}
			</span>
			<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
				{sub}
			</span>
		</div>
	);
}

function FounderLivePulse() {
	return (
		<span className="relative inline-flex size-1.5" aria-hidden="true">
			<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[color:var(--ticker-up)] opacity-70" />
			<span className="relative inline-flex size-1.5 rounded-full bg-[color:var(--ticker-up)]" />
		</span>
	);
}

function FounderStorySection({ founder }: { founder: FounderProfile }) {
	const items: Array<{
		Icon: typeof User;
		label: string;
		body: string;
	}> = [];
	if (founder.storyBio?.trim()) {
		items.push({ Icon: User, label: "Bio", body: founder.storyBio });
	}
	if (founder.storyWorkingStyle?.trim()) {
		items.push({
			Icon: Workflow,
			label: "Working style",
			body: founder.storyWorkingStyle,
		});
	}
	if (founder.storyStack?.trim()) {
		items.push({ Icon: Layers, label: "Stack", body: founder.storyStack });
	}
	if (founder.storyNext?.trim()) {
		items.push({ Icon: Rocket, label: "What's next", body: founder.storyNext });
	}

	if (items.length === 0) return null;

	return (
		<section className="mt-14">
			<div className="pb-6">
				<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Founder story
				</span>
			</div>
			<div className="border-t border-border pt-5">
				<div className="columns-1 gap-x-8 @2xl/page:columns-2">
					{items.map(({ Icon, label, body }) => (
						<div key={label} className="mb-5 flex break-inside-avoid gap-3">
							<div className="mt-0.5 flex size-7 shrink-0 items-center justify-center border border-border bg-background text-muted-foreground">
								<Icon className="size-3.5" />
							</div>
							<div className="min-w-0">
								<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
									&gt; {label}
								</p>
								<p className="mt-1 text-pretty text-base text-foreground">
									{body}
								</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
